/**
 * Pasaje de una fuente (del SourcePanel de 70e4cef). Carga el fragmento que mejor
 * coincide con el extracto de la cita desde GET /documents/:id/passage y lo
 * muestra resaltado; la vista inicial es corta y «Cargar más» despliega el resto.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { Citation } from '../../../services/chatbot.service';
import documentsService, { PassageChunk, PassageResponse } from '../../../services/documents.service';
import { findHighlightSpan } from '../../../utils/passageHighlight';

type LoadMoreDirection = 'prev' | 'next' | null;

function mergeChunks(existing: PassageChunk[], incoming: PassageChunk[]): PassageChunk[] {
  const byIndex = new Map<number, PassageChunk>();
  existing.forEach(c => byIndex.set(c.index, c));
  incoming.forEach(c => byIndex.set(c.index, c));
  return Array.from(byIndex.values()).sort((a, b) => a.index - b.index);
}

// Vista inicial (mockup «B · fuentes + asistente»): el fragmento citado recortado alrededor de la cita.
const CONTEXT_BEFORE = 100;
const CONTEXT_AFTER = 140;
const PLAIN_PREVIEW = 280;
// Línea de encabezado del documento («(ORD. N° 1407, DE 10.06.2026)»): ya la dicen el título y «tipo · año».
const HEADER_LINE = /^\s*\([^)\n]{3,80}\)\s*\n+/;

/** Corta en el espacio más cercano hacia afuera para no partir palabras. */
function snapStart(text: string, i: number) { if (i <= 0) return 0; const sp = text.lastIndexOf(' ', i); return sp > 0 ? sp + 1 : i; }
function snapEnd(text: string, i: number) { if (i >= text.length) return text.length; const sp = text.indexOf(' ', i); return sp > 0 ? sp : i; }

/** Contenido del fragmento con la cita (si se encuentra) en <mark>. Sin coincidencia no se resalta nada. */
function HighlightedChunk({ content, excerpt, isBest, preview }: { content: string; excerpt?: string | null; isBest: boolean; preview?: boolean }) {
  const span = useMemo(
    () => (isBest ? findHighlightSpan(excerpt, content, 40) : null),
    [isBest, content, excerpt]
  );

  if (!isBest) {
    return <p className="whitespace-pre-wrap text-slate-600 leading-relaxed">{content}</p>;
  }

  let from = 0;
  let to = content.length;
  if (preview) {
    const header = HEADER_LINE.exec(content);
    const bodyStart = header ? header[0].length : 0;
    if (span) {
      from = Math.max(bodyStart, snapStart(content, span.start - CONTEXT_BEFORE));
      to = snapEnd(content, span.end + CONTEXT_AFTER);
    } else {
      from = bodyStart;
      to = snapEnd(content, bodyStart + PLAIN_PREVIEW);
    }
  }
  const lead = from > 0 && preview ? '… ' : '';
  const tail = to < content.length ? ' …' : '';

  if (!span) {
    return <p className="whitespace-pre-wrap text-slate-600 leading-relaxed">{lead}{content.slice(from, to).trim()}{tail}</p>;
  }

  const markStart = Math.max(span.start, from);
  return (
    <p className="whitespace-pre-wrap text-slate-600 leading-relaxed">
      {lead}{content.slice(from, markStart)}
      <mark className="bg-amber-100 rounded px-0.5">{content.slice(markStart, span.end)}</mark>
      {content.slice(span.end, to)}{tail}
    </p>
  );
}

export type Passage = ReturnType<typeof usePassage>;

/** Estado y carga del pasaje de una cita; el encabezado del panel usa `data.document`. */
export function usePassage(citation: Citation | null) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PassageResponse | null>(null);
  const [chunks, setChunks] = useState<PassageChunk[]>([]);
  const [loadingMore, setLoadingMore] = useState<LoadMoreDirection>(null);

  const documentId = citation?.document_id || null;

  const fetchInitial = useCallback(async (docId: string, q: string | undefined) => {
    setLoading(true);
    setError(null);
    setData(null);
    setChunks([]);
    try {
      const res = await documentsService.getPassage(docId, { q });
      setData(res);
      setChunks(res.chunks);
    } catch {
      setError('No se pudo cargar la fuente');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!documentId) return;
    // Fall back to the citation title as the search query if no excerpt
    // was captured (backend endpoint still works, just less precise).
    const q = citation?.excerpt || citation?.titulo || undefined;
    fetchInitial(documentId, q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const loadedIndexes = chunks.map(c => c.index);
  const minLoaded = loadedIndexes.length ? Math.min(...loadedIndexes) : null;
  const maxLoaded = loadedIndexes.length ? Math.max(...loadedIndexes) : null;

  const allIndexes = data?.chunk_indexes || [];
  const canLoadPrev = !data?.no_chunks && minLoaded !== null && allIndexes.some(i => i < minLoaded);
  const canLoadNext = !data?.no_chunks && maxLoaded !== null && allIndexes.some(i => i > maxLoaded);

  const loadMore = async (direction: 'prev' | 'next') => {
    if (!documentId || minLoaded === null || maxLoaded === null) return;
    const candidates = direction === 'prev'
      ? allIndexes.filter(i => i < minLoaded)
      : allIndexes.filter(i => i > maxLoaded);
    if (candidates.length === 0) return;
    const target = direction === 'prev' ? Math.max(...candidates) : Math.min(...candidates);

    setLoadingMore(direction);
    try {
      const res = await documentsService.getPassage(documentId, { around: target });
      setChunks(prev => mergeChunks(prev, res.chunks));
    } catch {
      // Keep whatever is already loaded — non-fatal.
    } finally {
      setLoadingMore(null);
    }
  };

  return { loading, error, data, chunks, loadingMore, canLoadPrev, canLoadNext, loadMore };
}

/**
 * Pasaje desplegado bajo la fuente activa: línea índigo y cita resaltada en ámbar. La vista inicial muestra solo
 * el fragmento citado, recortado; «Cargar más», al final, despliega el fragmento completo y sus vecinos, y desde
 * ahí sigue cargando hacia ambos lados.
 */
export default function SourcePassage({ citation, passage }: { citation: Citation; passage: Passage }) {
  const { loading, error, data, chunks, loadingMore, canLoadPrev, canLoadNext, loadMore } = passage;
  const [expanded, setExpanded] = useState(false);

  const best = data ? chunks.find(c => c.index === data.best_chunk_index) ?? chunks[0] : undefined;
  const moreButton = (direction: 'prev' | 'next', onClick: () => void) => (
    <button
      onClick={onClick}
      disabled={loadingMore !== null}
      className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
    >
      {loadingMore === direction ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : direction === 'prev' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      Cargar más
    </button>
  );

  return (
    <div className="mt-2 pl-3 border-l-2 border-indigo-200">
      {loading && (
        <div className="space-y-3 animate-pulse">
          <div className="h-2.5 bg-slate-200 rounded w-5/6" />
          <div className="h-3 bg-slate-200 rounded w-full" />
          <div className="h-3 bg-slate-200 rounded w-3/4" />
          <div className="h-3 bg-slate-200 rounded w-full" />
          <div className="h-3 bg-slate-200 rounded w-2/3" />
        </div>
      )}

      {!loading && error && (
        <p className="text-slate-500">{error}</p>
      )}

      {!loading && !error && data && !expanded && best && (
        <div className="space-y-2">
          {data.no_chunks && (
            <p className="text-[11px] text-slate-400 italic">
              Este documento aún no está segmentado en fragmentos — mostrando el texto completo extraído.
            </p>
          )}
          <HighlightedChunk content={best.content} excerpt={citation.excerpt} isBest preview />
          {moreButton('next', () => setExpanded(true))}
        </div>
      )}

      {!loading && !error && data && expanded && (
        <div className="space-y-3">
          {canLoadPrev && moreButton('prev', () => loadMore('prev'))}

          <div className="space-y-4">
            {chunks.map(chunk => (
              <HighlightedChunk
                key={chunk.index}
                content={chunk.content}
                excerpt={citation.excerpt}
                isBest={chunk.index === data.best_chunk_index}
              />
            ))}
          </div>

          {canLoadNext && moreButton('next', () => loadMore('next'))}
        </div>
      )}
    </div>
  );
}
